import { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { theme } from "../../../theme";
import type { ClientInvoiceOverdueByCurrency } from "../../../models/ClientModel";
import { formatAmountWithCurrency } from "../../../utils/formatters";
import ProgressCircle from "../../atoms/ProgressCircle";
import BodySmall from "../../atoms/typography/BodySmall";
import Small from "../../atoms/typography/Small";
import ListTabBar from "../ListTabBar";
import Tooltip from "../Tooltip";

interface Props {
  overdueByCurrency: ClientInvoiceOverdueByCurrency[];
}

type ColorPair = { color: string; backgroundColor: string };

const DEFAULT_CURRENCY_CODE = "SEK";

const FIXED_CURRENCY_COLORS: Record<string, ColorPair> = {
  SEK: { color: theme.colors.blue, backgroundColor: theme.colors.blue20 },
  EUR: { color: theme.colors.pink, backgroundColor: theme.colors.pink20 },
  GBP: { color: theme.colors.orange, backgroundColor: theme.colors.orange20 },
};

const FALLBACK_COLORS: ColorPair[] = [
  { color: theme.colors.green, backgroundColor: theme.colors.green20 },
  { color: theme.colors.red, backgroundColor: theme.colors.red20 },
];

const getCurrencyColors = (currencyCodes: string[]) => {
  let fallbackIndex = 0;
  return Object.fromEntries(
    currencyCodes.map((code) => [
      code,
      FIXED_CURRENCY_COLORS[code] ??
        FALLBACK_COLORS[fallbackIndex++ % FALLBACK_COLORS.length],
    ]),
  ) as Record<string, ColorPair>;
};

const getOverdueFraction = (currency: ClientInvoiceOverdueByCurrency) =>
  currency.totalAmountIncludingVatOutstanding > 0
    ? currency.totalAmountIncludingVatOverdue /
      currency.totalAmountIncludingVatOutstanding
    : 0;

const Grid = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  column-gap: 8px;
  row-gap: 8px;
  align-items: start;
`;

const AmountRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const OtherCurrencies = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
`;

const CircleButton = styled.button`
  display: flex;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: none;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${theme.colors.primary};
    outline-offset: 2px;
  }
`;

const OverdueByCurrency: React.FC<Props> = ({ overdueByCurrency }) => {
  const { t } = useTranslation();
  const [selectedCode, setSelectedCode] = useState<string>();

  const selected =
    overdueByCurrency.find((c) => c.currencyCode === selectedCode) ??
    overdueByCurrency.find((c) => c.currencyCode === DEFAULT_CURRENCY_CODE) ??
    overdueByCurrency[0];
  const others = overdueByCurrency.filter((c) => c !== selected);
  const colors = getCurrencyColors(
    overdueByCurrency.map((c) => c.currencyCode),
  );

  const getText = (currency: ClientInvoiceOverdueByCurrency) =>
    `${formatAmountWithCurrency(currency.totalAmountIncludingVatOverdue, currency.currencyCode)} ${t("invoiceAssessment.of").toLowerCase()} ${formatAmountWithCurrency(currency.totalAmountIncludingVatOutstanding, currency.currencyCode)} (${t("invoiceAssessment.overdueInvoiceCount", { count: currency.numberOfInvoicesOverdue })})`;

  const label = (
    <Small color={theme.colors.textSecondary} fontweight="600">
      {t("invoiceAssessment.overdue")}
    </Small>
  );

  if (!selected) {
    return (
      <div>
        {label}
        <BodySmall>-</BodySmall>
      </div>
    );
  }

  const selectedAmount = (
    <AmountRow>
      <ProgressCircle
        fraction={getOverdueFraction(selected)}
        {...colors[selected.currencyCode]}
      />
      <BodySmall>{getText(selected)}</BodySmall>
    </AmountRow>
  );

  if (others.length === 0) {
    return (
      <div>
        {label}
        {selectedAmount}
      </div>
    );
  }

  return (
    <Grid>
      {label}
      <ListTabBar
        small
        tabs={overdueByCurrency.map((c) => ({
          id: c.currencyCode,
          tabLabel: (
            <Small
              fontweight={400}
              color={
                c === selected
                  ? theme.colors.primary
                  : theme.colors.textSecondary
              }
            >
              {c.currencyCode}
            </Small>
          ),
        }))}
        selectedId={selected.currencyCode}
        onSelectTabId={setSelectedCode}
      />
      {selectedAmount}
      <OtherCurrencies>
        {others.map((currency) => (
          <Tooltip key={currency.currencyCode} text={getText(currency)}>
            <CircleButton
              type="button"
              aria-label={getText(currency)}
              onClick={() => setSelectedCode(currency.currencyCode)}
            >
              <ProgressCircle
                fraction={getOverdueFraction(currency)}
                {...colors[currency.currencyCode]}
              />
            </CircleButton>
          </Tooltip>
        ))}
      </OtherCurrencies>
    </Grid>
  );
};

export default OverdueByCurrency;
